import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../../app/providers/AuthProvider";
import {
  createTreasuryForm,
  getTreasuryFormById,
  listTreasuryFormAccounts,
  listTreasuryFormCategories,
  treasuryFormHasAssignments,
  updateTreasuryForm,
} from "../api/treasuryFormsApi";
import { TREASURY_FORM_TYPE_OPTIONS } from "../constants/treasuryFormsOptions";
import {
  buildTreasuryFormPayload,
  makeTreasuryFormField,
  mapTreasuryFormServerErrors,
  normalizeTreasuryFormDetail,
  treasuryEditorSignature,
  treasuryFieldsSignature,
  validateTreasuryFormEditor,
} from "../utils/treasuryFormEditorModel";
import FormulariosHeader from "./FormulariosHeader";
import TreasuryFormFieldEditor from "./TreasuryFormFieldEditor";

function userDisplayName(user) {
  if (!user) return "Usuario logueado";
  const fullName = [user.first_name || user.firstName, user.last_name || user.lastName]
    .filter(Boolean)
    .join(" ");
  return (
    user.displayName ||
    user.display_name ||
    user.name ||
    fullName ||
    user.email ||
    "Usuario logueado"
  );
}

function responsePayload(response) {
  return response?.data && !response?.id ? response.data : response;
}

function mergeHistoricalReferences(items, fields, property, fallbackProperty) {
  const result = [...items];
  const ids = new Set(result.map((item) => String(item.id)));
  fields.forEach((field) => {
    const id = field[property];
    const reference = field[fallbackProperty];
    if (!id || ids.has(String(id))) return;
    result.push({
      id,
      name: reference?.name || `${fallbackProperty === "account" ? "Cuenta" : "Categoría"} #${id}`,
      isActive: false,
      deletedAt: true,
      ...(fallbackProperty === "category"
        ? { movementTypeScope: reference?.movementTypeScope || "BOTH" }
        : {}),
    });
    ids.add(String(id));
  });
  return result;
}

function InsertFieldZone({ onInsert, label = "Añadir campo aquí" }) {
  return (
    <Box
      sx={{
        height: 30,
        display: "flex",
        alignItems: "center",
        "& .insert-field": { opacity: { xs: 0.62, md: 0.05 } },
        "&:hover .insert-field, &:focus-within .insert-field": { opacity: 1 },
      }}
    >
      <Button
        className="insert-field"
        onClick={onInsert}
        startIcon={<AddIcon />}
        size="small"
        fullWidth
        sx={{
          height: 26,
          border: "1px dashed",
          borderColor: "primary.300",
          borderRadius: "9px",
          textTransform: "none",
          fontSize: 11,
          transition: "opacity 150ms ease",
        }}
      >
        {label}
      </Button>
    </Box>
  );
}

export default function TreasuryFormEditor({ mode, formId }) {
  const isEditing = mode === "edit";
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const listState = location.state?.listState;
  const returnTo = location.state?.returnTo || "/tesoreria/formularios";

  const [metadata, setMetadata] = useState({ name: "", type: "", description: "" });
  const [fields, setFields] = useState(() => [makeTreasuryFormField()]);
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [financialLoading, setFinancialLoading] = useState(true);
  const [financialError, setFinancialError] = useState("");
  const [loading, setLoading] = useState(isEditing);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [serverMetadataErrors, setServerMetadataErrors] = useState({});
  const [serverFieldErrors, setServerFieldErrors] = useState({});
  const [touchedMetadata, setTouchedMetadata] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [fieldsCollectionTouched, setFieldsCollectionTouched] = useState(false);
  const [versionNumber, setVersionNumber] = useState(1);
  const [hasAssignments, setHasAssignments] = useState(false);
  const [assignmentCheckFailed, setAssignmentCheckFailed] = useState(false);
  const [archived, setArchived] = useState(false);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const initialEditorSignatureRef = useRef(null);
  const initialFieldsSignatureRef = useRef(null);

  const editorSignature = useMemo(
    () => treasuryEditorSignature(metadata, fields),
    [metadata, fields]
  );
  const fieldsSignature = useMemo(() => treasuryFieldsSignature(fields), [fields]);

  useEffect(() => {
    if (!isEditing && initialEditorSignatureRef.current === null) {
      initialEditorSignatureRef.current = editorSignature;
      initialFieldsSignatureRef.current = fieldsSignature;
    }
  }, [editorSignature, fieldsSignature, isEditing]);

  useEffect(() => {
    const controller = new AbortController();
    setFinancialLoading(true);
    setFinancialError("");
    Promise.all([
      listTreasuryFormAccounts({ signal: controller.signal }),
      listTreasuryFormCategories({ signal: controller.signal }),
    ])
      .then(([accountItems, categoryItems]) => {
        if (controller.signal.aborted) return;
        setAccounts(accountItems);
        setCategories(categoryItems);
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        setFinancialError(
          error?.message || "No se pudieron cargar las cuentas y categorías."
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setFinancialLoading(false);
      });
    return () => controller.abort();
  }, [reloadKey]);

  useEffect(() => {
    if (!isEditing || !formId) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setLoadError("");

    Promise.all([
      getTreasuryFormById(Number(formId), { signal: controller.signal }),
      treasuryFormHasAssignments(Number(formId), { signal: controller.signal }).catch(() => {
        setAssignmentCheckFailed(true);
        return false;
      }),
    ])
      .then(([response, assignmentsExist]) => {
        if (controller.signal.aborted) return;
        const detail = responsePayload(response);
        const normalized = normalizeTreasuryFormDetail(detail);
        setMetadata(normalized.metadata);
        setFields(normalized.fields);
        setVersionNumber(normalized.versionNumber);
        setHasAssignments(assignmentsExist);
        setArchived(detail?.status === "ARCHIVED");
        setTouchedMetadata({});
        setTouchedFields({});
        setFieldsCollectionTouched(false);
        setServerMetadataErrors({});
        setServerFieldErrors({});
        initialEditorSignatureRef.current = treasuryEditorSignature(
          normalized.metadata,
          normalized.fields
        );
        initialFieldsSignatureRef.current = treasuryFieldsSignature(normalized.fields);
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        setLoadError(error?.message || "No se pudo cargar el formulario.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [formId, isEditing, reloadKey]);

  const isDirty =
    initialEditorSignatureRef.current !== null &&
    initialEditorSignatureRef.current !== editorSignature;
  const bodyChanged =
    initialFieldsSignatureRef.current !== null &&
    initialFieldsSignatureRef.current !== fieldsSignature;

  const mergedAccounts = useMemo(
    () => mergeHistoricalReferences(accounts, fields, "accountId", "account"),
    [accounts, fields]
  );
  const mergedCategories = useMemo(
    () => mergeHistoricalReferences(categories, fields, "categoryId", "category"),
    [categories, fields]
  );

  const validation = useMemo(
    () =>
      validateTreasuryFormEditor(metadata, fields, {
        accounts: mergedAccounts,
        categories: mergedCategories,
        requireActiveReferences: !isEditing || bodyChanged,
      }),
    [bodyChanged, fields, isEditing, mergedAccounts, mergedCategories, metadata]
  );

  const visibleMetadataErrors = {
    ...(touchedMetadata.name && validation.metadataErrors.name
      ? { name: validation.metadataErrors.name }
      : {}),
    ...(touchedMetadata.type && validation.metadataErrors.type
      ? { type: validation.metadataErrors.type }
      : {}),
    ...(fieldsCollectionTouched && validation.metadataErrors.fields
      ? { fields: validation.metadataErrors.fields }
      : {}),
    ...serverMetadataErrors,
  };
  const visibleFieldErrors = fields.reduce((result, field) => {
    result[field.localId] = {
      ...(touchedFields[field.localId]
        ? validation.fieldErrors[field.localId] || {}
        : {}),
      ...(serverFieldErrors[field.localId] || {}),
    };
    return result;
  }, {});

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!isDirty || saving) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty, saving]);

  const navigateBack = () => {
    navigate(returnTo, { state: { listState } });
  };

  const requestCancel = () => {
    if (isDirty) setConfirmCancelOpen(true);
    else navigateBack();
  };

  const insertField = (index) => {
    setFields((current) => {
      const next = [...current];
      next.splice(index, 0, makeTreasuryFormField());
      return next;
    });
    setFieldsCollectionTouched(true);
    setSaveError("");
  };

  const updateField = (updatedField) => {
    setFields((current) =>
      current.map((field) =>
        field.localId === updatedField.localId ? updatedField : field
      )
    );
    setServerFieldErrors((current) => {
      const next = { ...current };
      delete next[updatedField.localId];
      return next;
    });
    setTouchedFields((current) => ({ ...current, [updatedField.localId]: true }));
    setSaveError("");
  };

  const deleteField = (localId) => {
    setFields((current) => current.filter((field) => field.localId !== localId));
    setTouchedFields((current) => {
      const next = { ...current };
      delete next[localId];
      return next;
    });
    setFieldsCollectionTouched(true);
    setSaveError("");
  };

  const moveField = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= fields.length) return;
    setFields((current) => {
      const next = [...current];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  const handleMetadataChange = (property) => (event) => {
    setMetadata((current) => ({ ...current, [property]: event.target.value }));
    setTouchedMetadata((current) => ({ ...current, [property]: true }));
    setServerMetadataErrors((current) => {
      const next = { ...current };
      delete next[property];
      return next;
    });
    setSaveError("");
  };

  const handleSave = async () => {
    if (!validation.isValid || saving || archived) return;
    setSaving(true);
    setSaveError("");
    setServerMetadataErrors({});
    setServerFieldErrors({});
    try {
      const includeFields = !isEditing || bodyChanged;
      const payload = buildTreasuryFormPayload(metadata, fields, { includeFields });
      if (isEditing) await updateTreasuryForm(Number(formId), payload);
      else await createTreasuryForm(payload);

      navigate(returnTo, {
        replace: true,
        state: {
          listState,
          flashMessage: {
            severity: "success",
            message: isEditing
              ? "Formulario actualizado correctamente."
              : "Formulario creado correctamente.",
          },
        },
      });
    } catch (error) {
      const mapped = mapTreasuryFormServerErrors(error, fields);
      setSaveError(mapped.message);
      setServerMetadataErrors(mapped.metadataErrors);
      setServerFieldErrors(mapped.fieldErrors);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Stack alignItems="center" justifyContent="center" spacing={1.5} sx={{ minHeight: 360 }}>
        <CircularProgress />
        <Typography>Cargando formulario...</Typography>
      </Stack>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ p: 2 }}>
        <FormulariosHeader title="Editar formulario" onBack={requestCancel} />
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          action={<Button onClick={() => setReloadKey((value) => value + 1)}>Reintentar</Button>}
        >
          {loadError}
        </Alert>
      </Box>
    );
  }

  if (archived) {
    return (
      <Box sx={{ p: 2 }}>
        <FormulariosHeader title="Editar formulario" onBack={navigateBack} />
        <Alert severity="warning" sx={{ mt: 2 }}>
          Este formulario está archivado. Restauralo desde el listado antes de editarlo.
        </Alert>
      </Box>
    );
  }

  const showVersionWarning = isEditing && bodyChanged && (hasAssignments || assignmentCheckFailed);
  const saveDisabled =
    !validation.isValid || saving || (isEditing && !isDirty) || financialLoading;
  const roundedInputSx = {
    "& .MuiOutlinedInput-root": { borderRadius: "16px" },
  };

  return (
    <Box sx={{ width: "100%", p: { xs: 1, sm: 2 } }}>
      <Stack spacing={2}>
        <FormulariosHeader
          title={isEditing ? "Editar formulario" : "Crear formulario"}
          onBack={requestCancel}
        />

        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, sm: 2.5 },
            borderRadius: "22px",
            bgcolor: "rgba(255,255,255,0.90)",
            boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "minmax(260px, 0.8fr) minmax(430px, 1.7fr)" },
              gap: { xs: 2.5, lg: 3 },
              alignItems: "start",
            }}
          >
            <Stack spacing={1.5}>
              <Typography sx={{ fontSize: 15, fontWeight: 900, color: "primary.900" }}>
                Información general
              </Typography>

              <TextField
                size="small"
                label="Nombre del formulario *"
                value={metadata.name}
                onChange={handleMetadataChange("name")}
                inputProps={{ maxLength: 255 }}
                error={Boolean(visibleMetadataErrors.name)}
                helperText={visibleMetadataErrors.name}
                sx={roundedInputSx}
                fullWidth
              />

              <TextField
                select
                size="small"
                label="Tipo de formulario *"
                value={metadata.type}
                onChange={handleMetadataChange("type")}
                error={Boolean(visibleMetadataErrors.type)}
                helperText={visibleMetadataErrors.type}
                sx={roundedInputSx}
                fullWidth
              >
                {TREASURY_FORM_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                multiline
                minRows={4}
                label="Descripción / instrucciones"
                value={metadata.description}
                onChange={handleMetadataChange("description")}
                sx={roundedInputSx}
                fullWidth
              />

              <TextField
                disabled
                size="small"
                label="Estado"
                value="Activo"
                sx={roundedInputSx}
                fullWidth
              />
              <TextField
                disabled
                size="small"
                label="Autor"
                value={userDisplayName(user)}
                sx={roundedInputSx}
                fullWidth
              />

              {isEditing ? (
                <Typography sx={{ fontSize: 12, color: "neutral.600" }}>
                  Versión vigente: {versionNumber}
                </Typography>
              ) : null}

              {saveError ? (
                <Alert severity="error" sx={{ width: "100%" }}>
                  {saveError}
                </Alert>
              ) : null}

              <Stack direction="row" spacing={1} justifyContent="center" sx={{ pt: 0.5 }}>
                <Button onClick={requestCancel} disabled={saving} sx={{ textTransform: "none" }}>
                  Cancelar
                </Button>
                <Tooltip
                  title={
                    !validation.isValid
                      ? "Completá la información obligatoria y corregí los campos inválidos."
                      : isEditing && !isDirty
                      ? "No hay cambios para guardar."
                      : ""
                  }
                >
                  <span>
                    <Button
                      variant="contained"
                      startIcon={
                        saving ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <SaveOutlinedIcon />
                        )
                      }
                      disabled={saveDisabled}
                      onClick={handleSave}
                      sx={{
                        borderRadius: "22px",
                        textTransform: "none",
                        fontWeight: 900,
                        minWidth: 130,
                      }}
                    >
                      {saving ? "Guardando..." : "Guardar"}
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>

            <Stack spacing={0}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 900, color: "primary.900" }}>
                  Cuerpo
                </Typography>
                <Typography sx={{ fontSize: 11, color: "neutral.600" }}>
                  {fields.length} {fields.length === 1 ? "campo" : "campos"}
                </Typography>
              </Stack>

              {visibleMetadataErrors.fields ? (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {visibleMetadataErrors.fields}
                </Alert>
              ) : null}

              {fields.map((field, index) => (
                <Box key={field.localId}>
                  <TreasuryFormFieldEditor
                    field={field}
                    index={index}
                    total={fields.length}
                    errors={visibleFieldErrors[field.localId]}
                    accounts={mergedAccounts}
                    categories={mergedCategories}
                    financialLoading={financialLoading}
                    financialError={financialError}
                    onChange={updateField}
                    onDelete={deleteField}
                    onMove={moveField}
                  />
                  {index < fields.length - 1 ? (
                    <InsertFieldZone onInsert={() => insertField(index + 1)} />
                  ) : null}
                </Box>
              ))}

              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => insertField(fields.length)}
                fullWidth
                sx={{ mt: 1.2, borderRadius: "10px", textTransform: "none", fontWeight: 800 }}
              >
                Añadir campo
              </Button>
            </Stack>
          </Box>

          {showVersionWarning ? (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {hasAssignments
                ? "Este formulario tiene asignaciones. Al guardar cambios en el cuerpo se creará una versión nueva; las asignaciones anteriores conservarán su versión."
                : "No se pudo verificar si existen asignaciones. Si las hubiera, el backend conservará su versión y creará una nueva para estos cambios."}
            </Alert>
          ) : null}

        </Paper>
      </Stack>

      <Dialog open={confirmCancelOpen} onClose={() => setConfirmCancelOpen(false)}>
        <DialogTitle>Cambios sin guardar</DialogTitle>
        <DialogContent>
          <Typography>Tenés cambios sin guardar. ¿Querés salir igualmente?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmCancelOpen(false)}>Seguir editando</Button>
          <Button color="error" onClick={navigateBack}>Salir sin guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
